import { useTranslation } from 'react-i18next'
import { FileText } from '@digital-twins/design-system/icons'
import { AssistantEvidenceItem } from '@digital-twins/shared-types/assistant'
import {
  KnowledgeAskCitation,
  KnowledgeDocumentListItem,
  SOCIAL_PLATFORMS,
  SocialPlatform,
} from '@digital-twins/shared-types'
import { SourceIcon } from '@/components/citations'
import { Text } from '@/components/ui/Text'
import { SocialPlatformIcon } from '@/pages/KnowledgeHubPage/social/SocialPlatformIcon'
import { citationSourceForTarget } from './citationSources'
import { furtherReadingItems } from './furtherReadingItems'
import { hostnameFromUrl, SourceFavicon } from './ThreadWebSourceCard'

/** Below this many uncited sources there is no real surplus to offer. */
const MIN_FURTHER_READING = 2

interface ThreadFurtherReadingProps {
  /** Nest-built `further_reading`: offered sources the answer read but did not cite. */
  items?: Array<AssistantEvidenceItem> | null
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  /** The same handler a library citation chip uses to open the source view. */
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
  /** Study and room rows open an in-app route (study results, chat room). */
  onNavigate?: (path: string) => void
}

function isSocialPlatform(value: string): value is SocialPlatform {
  return (SOCIAL_PLATFORMS as ReadonlyArray<string>).includes(value)
}

const ROW_CLASS =
  'flex w-full min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left text-muted-foreground hover:bg-muted hover:text-foreground'

/**
 * A short list of sources the turn read but did not cite, so the prose only
 * cites to prove a point while useful extra sources stay one click away.
 */
export function ThreadFurtherReading(props: ThreadFurtherReadingProps) {
  const { items, docsByToken, onOpenCitation, onNavigate } = props

  const { t } = useTranslation('app')

  const rows = furtherReadingItems(items)
  if (rows.length < MIN_FURTHER_READING) {
    return null
  }

  return (
    <section className="max-w-prose space-y-1 pt-3" data-testid="thread-further-reading">
      <Text as="p" size="sm" color="muted">
        {t('threads.detail.furtherReading')}
      </Text>
      <ul className="space-y-0.5">
        {rows.map((item, index) => {
          const testId = `thread-further-reading-item-${index}`
          if (item.kind === 'web') {
            const hostname = hostnameFromUrl(item.url)
            const title = item.title?.trim() || item.publisher?.trim() || hostname || item.url
            const meta = item.publisher?.trim() || hostname
            return (
              <li key={item.id}>
                <a href={item.url} target="_blank" rel="noopener noreferrer" className={ROW_CLASS} data-testid={testId}>
                  <SourceFavicon hostname={hostname} />
                  <Text as="span" size="sm" className="truncate">{title}</Text>
                  {meta && meta !== title ? (
                    <Text as="span" size="xs" color="muted" className="shrink-0">{meta}</Text>
                  ) : null}
                </a>
              </li>
            )
          }
          if (item.kind === 'social' && item.postRef) {
            const platform = item.postRef.platform
            const handle = item.author?.trim() ? `@${item.author.trim().replace(/^@/, '')}` : null
            const label = handle || item.title?.trim() || hostnameFromUrl(item.postRef.url) || item.postRef.url
            return (
              <li key={item.id}>
                <a
                  href={item.postRef.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ROW_CLASS}
                  data-testid={testId}
                >
                  {isSocialPlatform(platform)
                    ? <SocialPlatformIcon platform={platform} className="h-3.5 w-3.5" />
                    : <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                  <Text as="span" size="sm" className="truncate">{label}</Text>
                </a>
              </li>
            )
          }
          if (item.kind === 'study' || item.kind === 'room') {
            // The same adapter the pills use: name, location and route stay in step.
            const source = citationSourceForTarget(
              item.kind === 'study'
                ? { kind: 'study', sourceKind: 'study', evidence: item }
                : { kind: 'room', sourceKind: 'room', evidence: item },
              index + 1,
              { t, resolveLabel: (citation) => citation.documentName, onOpenCitation, onNavigate },
            )
            return (
              <li key={item.id}>
                <button type="button" className={ROW_CLASS} onClick={source.onOpen} data-testid={testId}>
                  <SourceIcon source={source} className="h-3.5 w-3.5" />
                  <Text as="span" size="sm" className="truncate">{source.name}</Text>
                  {source.location ? (
                    <Text as="span" size="xs" color="muted" className="shrink-0">{source.location}</Text>
                  ) : null}
                </button>
              </li>
            )
          }
          if (item.kind !== 'library' && item.kind !== 'social') {
            return null
          }
          const documentName = item.title?.trim()
            || docsByToken?.get(item.documentToken)?.originalFilename
            || item.documentToken
          const citation: KnowledgeAskCitation = {
            documentName,
            documentToken: item.documentToken,
            snippet: item.snippet,
            granularity: item.page != null ? 'page' : 'document',
            extractionVersion: '',
            pageNumber: item.page ?? null,
            ...(item.citationToken ? { citationToken: item.citationToken } : {}),
          }
          return (
            <li key={item.id}>
              <button
                type="button"
                className={ROW_CLASS}
                onClick={() => onOpenCitation(index + 1, citation)}
                data-testid={testId}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <Text as="span" size="sm" className="truncate">{documentName}</Text>
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
